/**
 * EMS SSO bridge for the Android scanner app.
 *
 * The scanner cannot register its own redirect URI with EMS (the `WMS`
 * system's allow-list is EMS-owned data), so it borrows this web app's
 * registered callback: it opens a Custom Tab at
 *
 *   /auth/login?source=android&return=wms-scanner%3A%2F%2Fsso%2Fcallback
 *
 * We stash the validated request in sessionStorage (it must survive the
 * full-page EMS round trip), and when EMS lands on `/auth/callback` we hand
 * the EMS JWT straight back to the app via the return URI's FRAGMENT
 * (`wms-scanner://sso/callback#token=…`) — the fragment never reaches any
 * server or access log, and the token is NOT exchanged for a web session
 * (the app performs its own `/api/auth/device-exchange`).
 *
 * `return` is validated against a hard allow-list of app schemes on both
 * capture and completion; anything else is ignored and the normal web flow
 * runs. On EMS failure the error code is forwarded as `#error=…` so the app
 * can present it.
 */

const STORAGE_KEY = 'wms:android-sso';

/** Exact-match allow-list — never patterns, never user input. */
const ALLOWED_RETURNS = ['wms-scanner://sso/callback'] as const;

type AllowedReturn = (typeof ALLOWED_RETURNS)[number];

function asAllowedReturn(value: string | null): AllowedReturn | null {
  return (ALLOWED_RETURNS as readonly string[]).includes(value ?? '')
    ? (value as AllowedReturn)
    : null;
}

/**
 * Reads `?source=android&return=…` from a login-page URL. Returns true when a
 * valid Android SSO request was captured (caller should immediately start the
 * EMS redirect).
 */
export function captureAndroidSsoRequest(search: string): boolean {
  const params = new URLSearchParams(search);
  if (params.get('source') !== 'android') return false;
  const ret = asAllowedReturn(params.get('return'));
  if (!ret) return false;
  sessionStorage.setItem(STORAGE_KEY, ret);
  return true;
}

function takePendingReturn(): AllowedReturn | null {
  const ret = asAllowedReturn(sessionStorage.getItem(STORAGE_KEY));
  sessionStorage.removeItem(STORAGE_KEY);
  return ret;
}

type Replace = (url: string) => void;

const defaultReplace: Replace = (url) => {
  window.location.replace(url);
};

/**
 * If an Android SSO request is pending, forwards the EMS JWT to the app and
 * returns true — the web callback must then do NOTHING else with the token.
 */
export function completeAndroidSso(emsToken: string, replace: Replace = defaultReplace): boolean {
  const ret = takePendingReturn();
  if (!ret) return false;
  replace(`${ret}#token=${encodeURIComponent(emsToken)}`);
  return true;
}

/** Same, for the EMS error path (`#error=<code>`). */
export function failAndroidSso(code: string, replace: Replace = defaultReplace): boolean {
  const ret = takePendingReturn();
  if (!ret) return false;
  replace(`${ret}#error=${encodeURIComponent(code)}`);
  return true;
}
